import { DispatchLeg, isExportJob } from '@api/Trinium/Endpoints/Dispatch/types';
import DateSlot from '@components/DataGrid/DateSlot';
import RenderCell from '@components/DataGrid/RenderCell';
import { defaultSortComparator, EditableCellRef, EditSaveScope } from '@components/DataGrid/types';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { forwardRef, RefObject, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { EditableCell, resolveEditTargetRows } from '../types';
import { usePutLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { getApiErrorMessage } from '@api/getApiError';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { apiIsoStringFormat, parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';
import DatePicker from '@forms/Components/DatePicker';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { GridColDefExtended } from '@mui/x-data-grid-pro';

const headerDisplayName = 'LFD / Cutoff';

const lastFreeDayDefinition: GridColDefExtended<DispatchLeg> = {
  field: 'Last Free Day',
  minWidth: 132,
  width: 132,
  headerName: 'LFD/Cutoff',
  headerDisplayName,
  renderHeader: () => <RenderHeader primaryText={headerDisplayName} />,
  renderCell: ({ row }) => {
    const isExport = isExportJob(row.jobCatTransType);
    return (
      <RenderCell
        editable
        primarySlot={
          isExport ? (
            <DateSlot
              toDate={row.cutoffDate}
              dateOnly
              showTimeInTitle
              usePastDueStyle
            />
          ) : (
            <DateSlot
              toDate={row.lastFreeDayDate}
              dateOnly
            />
          )
        }
      />
    );
  },
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const isExport = isExportJob(row.jobCatTransType);
        const date = isExport ? row.cutoffDate : row.lastFreeDayDate;
        return caseInsensitiveIncludes(value, date);
      });
  },
  valueGetter: (_, row: DispatchLeg) =>
    isExportJob(row.jobCatTransType) ? (row.cutoffDate?.date ?? '') : (row.lastFreeDayDate?.date ?? ''),
  sortable: true,
  sortComparator: defaultSortComparator,
  /** Imports edit the Last Free Day and exports the Cutoff, so a batch save needs every selected order to be the same type */
  disableBatchEdit: (selectedRows: DispatchLeg[]) => new Set(selectedRows.map((selectedRow) => isExportJob(selectedRow.jobCatTransType))).size > 1,
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>, selectedRows?: DispatchLeg[]) => {
    if (row.legType !== 'FCL') return null;
    return (
      <LastFreeDayEditCell
        row={row}
        selectedRows={selectedRows}
        ref={ref}
      />
    );
  }
};

export default lastFreeDayDefinition;

const LastFreeDayEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row, selectedRows }, ref) => {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [putLegs] = usePutLegsMutation();
  const isExport = isExportJob(row.jobCatTransType);
  const initValue = useMemo(
    () => (isExport ? parseDayJs(row.cutoffDate?.unix) : parseDayJs(row.lastFreeDayDate?.unix)),
    [isExport, row.cutoffDate, row.lastFreeDayDate]
  );
  const [dateValue, setDateValue] = useState(initValue);
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));

  const saveHandler = useCallback(
    (scope: EditSaveScope) => {
      // Only need to call API if they changed the value
      if (
        (dateValue?.isValid() && initValue?.isValid() && dateValue.diff(initValue) !== 0) ||
        (initValue?.isValid() && !dateValue) ||
        (!initValue && dateValue?.isValid())
      ) {
        const formattedDate = dateValue?.format(apiIsoStringFormat) ?? null;
        const targetRows = resolveEditTargetRows(scope, row, selectedRows, (targetRow) => targetRow.legType === 'FCL');

        // Leaving availDate off keeps each leg's own Full Available date
        void putLegs({
          terminalNumber,
          actionType: {
            action: 'setAvailability',
            data: {
              legIds: targetRows.map((targetRow) => targetRow.legId),
              ...(isExport ? { cutoffDate: formattedDate } : { lastFreeDate: formattedDate })
            }
          }
        })
          .unwrap()
          .then((response) => {
            response.errors.forEach((error) => {
              appDispatch(appendErrorAnnouncement({ message: error.errorMessage }));
            });
          })
          .catch((error: unknown) => {
            appDispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
          });
      }
    },
    [appDispatch, terminalNumber, putLegs, dateValue, row, selectedRows, initValue, isExport]
  );

  /** Define the callable Ref functions */
  useImperativeHandle(ref, () => {
    return {
      saveChanges: saveHandler
    };
  }, [saveHandler]);

  return (
    <div style={{ height: '48px', maxWidth: '164px' }}>
      <DatePicker
        value={dateValue}
        onChange={(value) => {
          setDateValue(value ?? null);
        }}
        autoFocus
        disabled={isLoading}
        customProps={{
          labelText: isExport ? 'Cutoff' : 'Last Free Day',
          isDirty: false,
          errorText: undefined,
          onClear: () => {
            setDateValue(null);
          },
          variant: 'standard',
          disabledReason: isLoading ? 'Updating...' : undefined
        }}
      />
    </div>
  );
});
